import { useMemo, useState, useCallback, memo } from 'react'

import * as Styled from './styled'
import { DropdownHeader } from './Header'
import DropdownSkeleton from './Skeleton'
import { DropdownMainContent } from './MainContent'
import {
  IBaseDropdownItem,
  IDropdownProps,
  ISearchableDropdownProps
} from './types'
import { getFilteredItems, getActiveItem } from './utils'
import { useDropdownState } from 'hooks/useDropdownState'
import { SEARCH_NO_RESULT_FOUND_IMG } from 'consts/images'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { SlideUp, BackButton } from 'common-components'
import { useMobileInnerSliderCallbacks } from 'hooks/useMobileInnerSliderCallbacks'

const DropdownContent = ({
  items,
  title,
  className,
  searchKeys,
  onItemClick,
  selectedItemId,
  ChildComponent,
  noResultsImage = SEARCH_NO_RESULT_FOUND_IMG,
  scrollableParentRef
}: IDropdownProps<IBaseDropdownItem>) => {
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const { openCallback, closeCallback } = useMobileInnerSliderCallbacks()

  const { isOpen, toggleDropdown, closeDropdown } = useDropdownState(
    openCallback,
    closeCallback
  )

  const [searchQuery, setSearchQuery] = useState<string>('')

  const hasContent = useMemo(() => items.length > 1, [items]) //not filtered items, but the initial data

  const activeItem = useMemo(
    () => getActiveItem({ items, selectedItemId }),
    [items, selectedItemId]
  )

  const filteredItems = useMemo(
    () =>
      getFilteredItems({
        items,
        searchQuery,
        searchKeys
      }),
    [items, searchQuery, searchKeys]
  )

  const handleItemClick = useCallback(
    (id: string | number) => {
      onItemClick(id)
      closeDropdown()
    },
    [onItemClick, closeDropdown]
  )

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  const handleToggleDropdown = useCallback(() => {
    if (hasContent) {
      toggleDropdown()
    }
  }, [hasContent, toggleDropdown])

  const headerContent = useMemo(
    () => (
      <DropdownHeader
        isOpen={isOpen}
        activeItem={activeItem}
        onSearch={handleSearch}
        hasContent={hasContent}
        ChildComponent={ChildComponent}
      />
    ),
    [isOpen, activeItem, hasContent, handleSearch, ChildComponent]
  )

  const mainContent = useMemo(() => {
    if (hasContent) {
      return (
        <DropdownMainContent
          title={title}
          data={filteredItems}
          activeItem={activeItem}
          searchQuery={searchQuery}
          ChildComponent={ChildComponent}
          noResultsImage={noResultsImage}
          onListItemClick={handleItemClick}
        />
      )
    }
    return null
  }, [
    title,
    activeItem,
    searchQuery,
    hasContent,
    filteredItems,
    noResultsImage,
    ChildComponent,
    handleItemClick
  ])

  if (isTablet && isOpen) {
    return (
      <SlideUp isOpen={isOpen} onClose={closeDropdown} bottomMargin={0}>
        <Styled.TabletView>
          <BackButton onClick={closeDropdown} />
          {mainContent}
        </Styled.TabletView>
      </SlideUp>
    )
  }

  return (
    <Styled.SearchableDropdownWrapper className={className}>
      <Styled.SearchableDropdownContent
        headerContent={headerContent}
        mainContent={mainContent}
        isOpen={isOpen}
        toggleDropdown={handleToggleDropdown}
        closeDropdown={closeDropdown}
        scrollableParentRef={scrollableParentRef}
      />
    </Styled.SearchableDropdownWrapper>
  )
}

const SearchableDropdown = ({
  items,
  title,
  className,
  searchKeys,
  onItemClick,
  selectedItemId,
  ChildComponent,
  isLoading = false,
  noResultsImage = SEARCH_NO_RESULT_FOUND_IMG,
  scrollableParentRef
}: ISearchableDropdownProps<IBaseDropdownItem>) => {
  if (isLoading || !items) {
    return (
      <Styled.SearchableDropdownWrapper
        className={className}
        $isLoading={isLoading}
      >
        <DropdownSkeleton />
      </Styled.SearchableDropdownWrapper>
    )
  }

  return (
    <DropdownContent
      items={items}
      title={title}
      searchKeys={searchKeys}
      selectedItemId={selectedItemId}
      onItemClick={onItemClick}
      ChildComponent={ChildComponent}
      noResultsImage={noResultsImage}
      scrollableParentRef={scrollableParentRef}
    />
  )
}

export default memo(SearchableDropdown)
